import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createFreeTrialAccount } from "../api/api";
import { API_RESPONSE_ERROR_CODES } from "../utils/constants";
import '../css/account-create.css';


const AccountCreateComponent = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    companyName: "",
    email: "",
    appTypeCode: null,
    appTypeId: null,
    applicationName: "",
    appUrl: ""
  });
  const [errors, setErrors] = useState({});
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;

  const errorMessage = validateField(name, value);
  setFormData((prev) => ({ ...prev, [name]: value }));
  setErrors((prev) => ({
    ...prev,
    [name]: errorMessage
  }));
  };

  useEffect(() => {
    function sendHeight() {
      const height = document.documentElement.scrollHeight;
      window.parent.postMessage({ iframeHeight: height }, "*");
    }

    sendHeight(); // Send initial height
    window.addEventListener("resize", sendHeight);

    // Observe DOM changes
    const observer = new MutationObserver(() => {
      sendHeight();
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    return () => {
      window.removeEventListener("resize", sendHeight); // Cleanup on unmount
    };
  }, []);

  const handleOptionChange = (e) => {
    switch (e.target.value) {
      case "sfLight":
        setFormData((prev) => ({ ...prev, appTypeId: 1, appTypeCode: "sfLight", applicationName: "Salesforce" }));
        break;
      case "workday":
        setFormData((prev) => ({ ...prev, appTypeId: 4, appTypeCode: "workday", applicationName: "Workday" }));
        break;
      case "oracle":
        setFormData((prev) => ({ ...prev, appTypeId: 5, appTypeCode: "oracle", applicationName: "Oracle" }));
        break;
      case "dynamics365":
        setFormData((prev) => ({ ...prev, appTypeId: 7, appTypeCode: "dynamics365", applicationName: "Dynamics365" }));
        break;
      default:
        setFormData((prev) => ({ ...prev, appTypeId: 6, appTypeCode: "unknown", applicationName: "" }));
        break;
    }
    clearError("appTypeCode");
  };

  const validateField = (name, value) => {
    switch (name) {
      case "firstName":
      case "lastName":
        return value.trim() ? "" : "Invalid or empty name.";
      case "companyName":
        return value.trim() ? "" : "Enter company name.";
      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value) ? "" : "Enter a valid work email.";
      case "appUrl":
        const urlRegex = /^(https?:\/\/)?([\w\-])+\.{1}([a-zA-Z]{2,63})([\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/;
        return urlRegex.test(value) ? "" : "Enter a valid application URL.";
      case "applicationName":
        return value.trim() ? "" : "Enter application name.";
      case "appTypeCode":
        return !value ? "Select an application." : "";
      default:
        return "";
    }
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      console.log("Form submitted:", formData);
      setLoader(true);
      let hubSpotFormData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyName: formData.companyName,
        email: formData.email,
        appTypeCode: formData.appTypeCode === 'unknown' ? 'custom' : formData.appTypeCode,
        appUrl: formData.appUrl,
        applicationName: formData.applicationName,
        aptyFreeTrialAccountStatus: 'success',
        failureReason: ''
      };
      try {
        const { firstName, lastName, ...rest } = formData;
        const finalData = { ...rest, fullName: `${firstName} ${lastName}` };
        const result = await createFreeTrialAccount(finalData);
        setLoader(false);
        if (result.payload.status) {
          sendRequestToHubSpot(hubSpotFormData)
          navigate("/success");
        } else if (result.payload.errorCode === API_RESPONSE_ERROR_CODES.ALREADY_EXISTS) {
          hubSpotFormData = {
            ...hubSpotFormData,
            aptyFreeTrialAccountStatus: 'failure',
            failureReason: 'Account With this email already exists in Apty'
          }
          sendRequestToHubSpot(hubSpotFormData)
          navigate("/already-exists");
        } else if (result.payload.errorCode === API_RESPONSE_ERROR_CODES.EXPIRED) {
          hubSpotFormData = {
            ...hubSpotFormData,
            aptyFreeTrialAccountStatus: 'failure',
            failureReason: 'Account With this email got expired'
          }
          sendRequestToHubSpot(hubSpotFormData)
          navigate("/expired");
        } else if (result.payload.errorCode === API_RESPONSE_ERROR_CODES.DISPOSABLE_EMAIL) {
          setErrors((prev) => ({ ...prev, email: "Disposable email addresses are not allowed." }));
          hubSpotFormData = {
            ...hubSpotFormData,
            aptyFreeTrialAccountStatus: 'failure',
            failureReason: 'Account cannot be created using disposable email'
          }
          sendRequestToHubSpot(hubSpotFormData)
        } else if (result.payload.errorCode === API_RESPONSE_ERROR_CODES.PUBLIC_EMAIL) {
          setErrors((prev) => ({ ...prev, email: "Public email providers are not allowed, please provide a valid work email" }));
          hubSpotFormData = {
            ...hubSpotFormData,
            aptyFreeTrialAccountStatus: 'failure',
            failureReason: 'Account cannot be created using public email'
          }
          sendRequestToHubSpot(hubSpotFormData)
        } else if (result.payload.errorCode === API_RESPONSE_ERROR_CODES.INVALID_EMAIL_ADDRESS) {
          setErrors((prev) => ({ ...prev, email: "Invalid email address" }));
          hubSpotFormData = {
            ...hubSpotFormData,
            aptyFreeTrialAccountStatus: 'failure',
            failureReason: 'Invalid Email Address'
          }
          sendRequestToHubSpot(hubSpotFormData)
        } else if (result.payload.errorCode === API_RESPONSE_ERROR_CODES.PREMIUM_ACCOUNT_EXISTS) {
          setErrors((prev) => ({ ...prev, email: "Account with this email already exists in Apty Premium Subscription" }));
          hubSpotFormData = {
            ...hubSpotFormData,
            aptyFreeTrialAccountStatus: 'failure',
            failureReason: 'Premium account already exists with this email'
          }
          sendRequestToHubSpot(hubSpotFormData)
        }
      } catch (err) {
        hubSpotFormData = {
          ...hubSpotFormData,
          aptyFreeTrialAccountStatus: 'failure',
          failureReason: 'Internal Server Error'
        }
        sendRequestToHubSpot(hubSpotFormData)
        navigate("/failed");
      }
    }
  };

  const clearError = (name) => {
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const sendRequestToHubSpot = (data) => {
    window.parent.postMessage(data, "*");
  }

  return (
    <div class="form-hero-section bg-lblack section-padding account-create">
      <div class="form-container">
        <div class="form-hero-block">
          <div class="form-hero-content">
            <h1>
              <span class="gradient-text">Get more people to use</span> your SaaS application
            </h1>
            <div>
              <ul>
                <li>Create product workflows</li>
                <li>Personalize onboarding</li>
                <li>Identify user drop-off points</li>
              </ul>
            </div>
            <p class="fs-28">And drive product adoption effortlessly. All from one platform.</p>
          </div>
          <div class="form-hero-code">
            <div class="step-form-block">
              <form id="step-form" onSubmit={handleSubmit}>
                <div class="form-flex">
                  <div class="step-form-heading text-center">
                    <div class="fs-24 fw-500">Start a 15-day free trial of Apty’s Content Creation Studio</div>
                  </div>
                  <div class="form-inputs-block">
                    <div class="two-columns">
                      <div class="form-group">
                        <label for="first-name">First Name</label>
                        <input
                          type="text"
                          id="firstName"
                          placeholder="First Name"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                        />
                        {errors.firstName && <span className="error">{errors.firstName}</span>}
                      </div>
                      <div class="form-group">
                        <label for="last-name">Last Name</label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          placeholder="Last Name"
                        />
                        {errors.lastName && <span className="error">{errors.lastName}</span>}
                      </div>
                    </div>
                    <div class="form-group">
                      <label for="company-name">Company Name</label>
                      <input
                        type="text"
                        id="companyName"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        placeholder="Company Name" />
                      {errors.companyName && <span className="error">{errors.companyName}</span>}
                    </div>
                    <div class="form-group">
                      <label for="work-email">Work Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Work Email"
                      />
                      {errors.email && <span className="error">{errors.email}</span>}
                    </div>
                  </div>
                </div>

                <div class="form-flex">
                  <div class="step-form-heading text-center">
                    <div class="fs-24 fw-500">How would you like to try out Apty?</div>
                    <div class="fs-16">Select an application to try out Apty’s Content Creation Studio</div>
                  </div>
                  <div class="form-inputs-block">
                    <div class="radio-error">
                      <div class="radio-options">
                        <div class="radio-item">
                          <div class="radio-item-pos">
                            <img src="https://freetrial.demo.apty.io/gtm-logos/salesforce.svg" alt="Salesforce" />
                            <input
                              type="radio"
                              name="appTypeCode"
                              id="salesforce"
                              value="sfLight"
                              onChange={handleOptionChange}
                            />
                            <label htmlFor="salesforce">Salesforce</label>
                          </div>
                        </div>
                        <div class="radio-item">
                          <div class="radio-item-pos">
                            <img src="https://freetrial.demo.apty.io/gtm-logos/workday.svg" alt="Workday" />
                            <input
                              type="radio"
                              name="appTypeCode"
                              id="workday"
                              value="workday"
                              onChange={handleOptionChange} />
                            <label htmlFor="workday">Workday</label>
                          </div>
                        </div>
                        <div class="radio-item">
                          <div class="radio-item-pos">
                            <img src="https://freetrial.demo.apty.io/gtm-logos/oracle.svg" alt="Zendesk" />
                            <input
                              type="radio"
                              name="appTypeCode"
                              id="oracle"
                              value="oracle"
                              onChange={handleOptionChange}
                            />
                            <label htmlFor="oracle" >Oracle</label>
                          </div>
                        </div>
                        <div class="radio-item">
                          <div class="radio-item-pos">
                            <img src="https://freetrial.demo.apty.io/gtm-logos/dynamics.svg" alt="Dynamics365" />
                            <input
                              type="radio"
                              name="appTypeCode"
                              id="dynamics365"
                              value="dynamics365"
                              onChange={handleOptionChange}
                            />
                            <label htmlFor="jira" >Dynamics 365</label>
                          </div>
                        </div>
                        <div class="radio-item">
                          <div class="radio-item-pos">
                            <img src="https://freetrial.demo.apty.io/gtm-logos/custom.svg" alt="Custom" />
                            <input
                              type="radio"
                              id="custom"
                              name="appTypeCode"
                              value="unknown"
                              onChange={handleOptionChange}
                            />
                            <label htmlFor="custom">Custom</label>
                          </div>
                        </div>
                      </div>
                      {errors.appTypeCode && <span className="error">{errors.appTypeCode}</span>}
                    </div>
                    {
                      formData.appTypeCode === "unknown" &&
                      <div class="form-group">
                        <label for="application-name">Application Name</label>
                        <input
                          type="text"
                          id="applicationName"
                          name="applicationName"
                          value={formData.applicationName}
                          onChange={handleInputChange}
                          placeholder="Application Name"
                        />
                        {errors.applicationName && <span className="error">{errors.applicationName}</span>}
                      </div>
                    }
                    <div class="form-group">
                      <label for="app-url">Application URL</label>
                      <input
                        type="url"
                        id="appUrl"
                        name="appUrl"
                        value={formData.appUrl}
                        onChange={handleInputChange}
                        placeholder="Application URL"
                      />
                      {errors.appUrl && <span className="error">{errors.appUrl}</span>}
                    </div>
                    <div class="form-footer">
                      <button id="next-step-2" type="submit">Start 15 Day Free Trial</button>
                    </div>
                  </div>
                  {loader &&
                    <div class="page-loader">
                      <div id="loader"></div>
                    </div>
                  }
                </div>
              </form>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountCreateComponent;
